import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Text } from "styles/utility-components";

import { BulkSaveEntity } from "components/leads/saving/bulkSaveEntityConfig";
import { BulkSaveLeadButton } from "components/leads/saving/BulkSaveLeadButton";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import {
  ContactSearchInput,
  RevealContactsInput_Contacts,
} from "lib/generated/app-service-gql/graphql";
import { getUserFacingMessage } from "lib/graphqlErrors";
import { ContactSearchResult } from "lib/hooks/api/contacts/useContactSearch";
import { RevealContactsItem, useRevealContacts } from "lib/hooks/api/contacts/useRevealContacts";
import { EventNames, useTracking } from "lib/tracking";
import { RevealStatus } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import ContactCreditsPopover from "./ContactCreditsPopover";
import { contactRevealRef, contactRevealState, contactSaveEntity } from "./contactSource";
import { revealEventData, RevealOutcome } from "./contactTracking";
import ExportCognismContactsButton from "./ExportCognismContactsButton";

const BAD_REQUEST = 400;

type Props = {
  /** Currently-selected contacts (resolved from the visible page). */
  selectedContacts: ContactSearchResult[];
  onClearSelection: () => void;
  /** The active search — forwarded to the in-bar export (unused while a selection exists). */
  filter: ContactSearchInput;
  totalResults: number | null;
};

/**
 * A source-agnostic reveal ref: {source, id} where id is the source-native handle.
 * Cognism also carries the stable cognismContactId (the durable charge/dedup key).
 */
type RevealRef = RevealContactsInput_Contacts;

/**
 * Reveal refs worth sending — only fresh previews (revealable state) of either source. Rows
 * the company already holds (revealed this page or previously) are skipped: re-revealing
 * them would be a pointless re-spend / no-op.
 */
function revealRefs(contacts: ContactSearchResult[]): RevealRef[] {
  return contacts.flatMap((c): RevealRef[] => {
    if (contactRevealState(c) !== "revealable") return [];
    const ref = contactRevealRef(c);
    return ref ? [ref] : [];
  });
}

/**
 * Unified bulk action bar over both sources. Reveal targets the unrevealed rows in the
 * selection, of either source (rows the company holds are flipped by search). Save-to-lead
 * covers both sources via APP-4677's bulk save: Oscar by oscar_contacts.id (`contact`),
 * Cognism by redeemId (`contact_cognism`, redeem-on-save).
 */
export default function CognismContactsActionBar({
  selectedContacts,
  onClearSelection,
  filter,
  totalResults,
}: Props) {
  const { mutate, isLoading } = useRevealContacts();
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  // Refs worth revealing — rows the company already holds are skipped.
  const refs = revealRefs(selectedContacts);
  const revealableCount = refs.length;

  // One unified save-to-lead selection across both sources (via contactSaveEntity): Oscar by
  // oscar_contacts.id (`contact`), Cognism by redeemId (`contact_cognism`, redeem-on-save — APP-4677),
  // each carrying its `savedEntityId` for the already-saved lookup. A Cognism row with no redeemId
  // isn't saveable, so it's dropped. The backend accepts the mixed batch.
  const saveEntities: BulkSaveEntity[] = selectedContacts.flatMap((c): BulkSaveEntity[] => {
    const entity = contactSaveEntity(c);
    return entity ? [entity] : [];
  });

  const reveal = (toReveal: RevealRef[]) => {
    if (toReveal.length === 0) return;
    mutate(
      { input: { contacts: toReveal } },
      {
        onSuccess: (data) => {
          const perItem = data.revealContacts.perItem;
          logEvent(EventNames.contactsRevealed, {
            ...revealEventData("Bulk", countByStatus(perItem)),
            "Requested count": toReveal.length,
          });
          reportResult(perItem, reveal);
          // Revealed Cognism rows are now REDEEMED in the cache; clear selection so the
          // bar doesn't linger as "Reveal 0 selected". Retry uses the toast's failed refs.
          onClearSelection();
          // Revealing several contacts spends credits, so refresh the balance and usage history.
          if (toReveal.length > 1) {
            void queryClient.invalidateQueries(["contactsExportCredits"]);
            void queryClient.invalidateQueries(["contactsCreditUsageHistory"]);
          }
        },
        onError: (error) => {
          logEvent(EventNames.contactsRevealed, {
            ...revealEventData("Bulk", {
              revealedCount: 0,
              suppressedCount: 0,
              failedCount: toReveal.length,
            }),
            "Requested count": toReveal.length,
          });
          // A refused bulk reveal (not enough credits) says why; anything else is retryable.
          toast.error(
            getUserFacingMessage(error, [BAD_REQUEST]) ??
              "Couldn't reveal contacts. Please try again.",
          );
        },
      },
    );
  };

  const onRevealClick = () => {
    if (revealableCount === 0) {
      toast.success("Selected contacts are already revealed.");
      return;
    }
    reveal(refs);
  };

  return (
    <Container aria-label="Contacts bulk action bar">
      <Flex gap={16} alignItems="center">
        <Text color="white">{pluralise(selectedContacts.length, "contact")} selected:</Text>
        <ActionButtonWrapper>
          {revealableCount > 1 ? (
            // Revealing several contacts spends credits like an export, so it confirms the same way.
            <ContactCreditsPopover
              action="reveal"
              trigger={
                <Button icon="eye" loading={isLoading}>
                  Reveal {revealableCount} selected
                </Button>
              }
              title="Reveal selected contacts"
              marginalCredits={revealableCount}
              onConfirm={() => reveal(refs)}
              disabled={isLoading}
            />
          ) : (
            <Button icon="eye" loading={isLoading} onClick={onRevealClick}>
              Reveal {revealableCount} selected
            </Button>
          )}
        </ActionButtonWrapper>
        {saveEntities.length > 0 && (
          <ActionButtonWrapper>
            <BulkSaveLeadButton entities={saveEntities} />
          </ActionButtonWrapper>
        )}
        <ActionButtonWrapper>
          <ExportCognismContactsButton
            selectedContacts={selectedContacts}
            totalResults={totalResults}
            filter={filter}
            inActionBar
          />
        </ActionButtonWrapper>
      </Flex>
      <Flex gap={8} alignItems="center">
        <Button icon="xClose" onClick={onClearSelection}>
          Deselect all
        </Button>
      </Flex>
    </Container>
  );
}

/** Per-status totals of a bulk reveal — the shape both the toast and tracking need. */
function countByStatus(perItem: RevealContactsItem[]): RevealOutcome {
  return {
    revealedCount: perItem.filter((i) => i.status === RevealStatus.Revealed).length,
    suppressedCount: perItem.filter((i) => i.status === RevealStatus.Suppressed).length,
    failedCount: perItem.filter((i) => i.status === RevealStatus.ProviderError).length,
  };
}

/**
 * Toast the success/failure breakdown, offering a one-click retry of the failed refs.
 * SUPPRESSED contacts (GDPR opt-out, no contactable channels) are surfaced separately —
 * they're not failures and retrying them can never succeed, so they're excluded from retry.
 */
function reportResult(perItem: RevealContactsItem[], retry: (refs: RevealRef[]) => void) {
  const revealed = perItem.filter((i) => i.status === RevealStatus.Revealed).length;
  const suppressed = perItem.filter((i) => i.status === RevealStatus.Suppressed).length;
  const failedRefs: RevealRef[] = perItem
    .filter((i) => i.status === RevealStatus.ProviderError)
    // Cognism refs keep their stable id: a bulk reveal is charged by it, and refused without it.
    .map((i) => ({
      source: i.source,
      id: i.id,
      ...(i.cognismContactId ? { cognismContactId: i.cognismContactId } : {}),
    }));

  const suppressedSuffix = suppressed > 0 ? ` ${suppressed} unavailable (privacy).` : "";

  if (failedRefs.length === 0) {
    const message = `Revealed ${pluralise(revealed, "contact")}.${suppressedSuffix}`;
    if (suppressed > 0) toast.info(message);
    else toast.success(message);
    return;
  }

  toast.error(
    `Revealed ${pluralise(revealed, "contact")}.${suppressedSuffix} ${failedRefs.length} failed.`,
    { action: { label: "Retry", onClick: () => retry(failedRefs) } },
  );
}

const Container = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  height: 48,
  padding: 8,
  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

// Every CTA on the bar is a primary button sitting on the primary-coloured bar, so its
// white outline comes from this wrapper — same treatment as the leads/bids action bars.
const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));
